import React, { useEffect, useMemo, useState } from "react";
import axios from "../config/axiosConfig";
import DashboardLayout from "../layouts/DashboardLayout";
import {
  Alert,
  Button,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Pagination,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PageHeader from "../components/layout/PageHeader";
import RegisterNewsForm from "../components/forms/RegisterNewsForm";
import NoticiaCard from "../components/news/NoticiaCard";
import { splitTituloContenido, esVideoNoticia, extraerYoutubeId } from "../utils/noticiasHelpers";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE = 12;

const normalize = (value) =>
  String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();

const NoticiasPage = () => {
  const { user } = useAuth();
  const canManage = useMemo(() => {
    const role = normalize(user?.role);
    return role === "admin" || role === "gerente";
  }, [user?.role]);

  const [noticias, setNoticias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editando, setEditando] = useState(null);

  const [busqueda, setBusqueda] = useState("");
  const [sucursal, setSucursal] = useState("");
  const [soloVideo, setSoloVideo] = useState(false);

  useEffect(() => {
    fetchNoticias();
  }, []);

  const fetchNoticias = async () => {
    try {
      const response = await axios.get("/api/news", {
        params: { order: "desc", limit: 1000 },
      });
      setNoticias(Array.isArray(response.data) ? response.data : (response.data?.data ?? []));
    } catch (err) {
      console.error("Error al obtener noticias:", err);
      setError("No se pudo cargar la lista de noticias.");
    } finally {
      setCargando(false);
    }
  };

  // Sucursales que tienen al menos una noticia, para los filtros rápidos.
  const sucursales = useMemo(
    () => [...new Set(noticias.map((n) => n.sucursal).filter(Boolean))].sort((a, b) =>
      a === "Nacional" ? -1 : b === "Nacional" ? 1 : a.localeCompare(b)
    ),
    [noticias]
  );

  const filtradas = useMemo(() => {
    const q = normalize(busqueda);
    return noticias.filter((n) => {
      if (sucursal && n.sucursal !== sucursal) return false;
      const { titulo, cuerpo } = splitTituloContenido(n);
      if (soloVideo && !extraerYoutubeId(cuerpo) && !esVideoNoticia(n.archivo_url)) return false;
      if (!q) return true;
      return [titulo, cuerpo, n.usuario].some((v) => normalize(v).includes(q));
    });
  }, [noticias, busqueda, sucursal, soloVideo]);

  // Volver a la primera página cuando cambian los filtros.
  useEffect(() => {
    setPage(1);
  }, [busqueda, sucursal, soloVideo]);

  const hayFiltros = Boolean(busqueda.trim() || sucursal || soloVideo);
  // Sin filtros, la más reciente va destacada arriba de la primera página (y no se repite en la grilla).
  const destacadaItem = hayFiltros ? null : filtradas[0];
  const destacada = page === 1 ? destacadaItem : null;
  const resto = destacadaItem ? filtradas.slice(1) : filtradas;
  const totalPages = Math.max(1, Math.ceil(resto.length / PAGE_SIZE));
  const pageItems = resto.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const limpiarFiltros = () => {
    setBusqueda("");
    setSucursal("");
    setSoloVideo(false);
  };

  const handleRegisterClick = () => {
    setEditando(null);
    setOpenModal(true);
  };

  const handleEditClick = (noticia) => {
    const { titulo, cuerpo } = splitTituloContenido(noticia);
    setEditando({ ...noticia, titulo, contenido: cuerpo });
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setEditando(null);
  };

  const handleSuccess = () => {
    fetchNoticias();
    setOpenModal(false);
    setEditando(null);
  };

  const handleDelete = async (noticia) => {
    const { titulo } = splitTituloContenido(noticia);
    if (!window.confirm(`¿Eliminar la noticia "${titulo || "sin título"}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`/api/news/${noticia.id}`);
      fetchNoticias();
    } catch (err) {
      console.error("Error al eliminar la noticia:", err);
      setError("No se pudo eliminar la noticia.");
    }
  };

  const cambiarPagina = (_, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const acciones = {
    onEdit: canManage ? handleEditClick : undefined,
    onDelete: canManage ? handleDelete : undefined,
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Noticias"
        subtitle="Anuncios y novedades para todo el equipo"
        backTo="/dashboard"
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleRegisterClick}>
            Nueva noticia
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: "auto" }}>
        {/* Filtros */}
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <TextField
              placeholder="Buscar por título, contenido o autor..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              size="small"
              sx={{ flex: 1, maxWidth: { sm: 460 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: busqueda && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setBusqueda("")} aria-label="Limpiar búsqueda">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={<Switch size="small" checked={soloVideo} onChange={(e) => setSoloVideo(e.target.checked)} />}
              label={<Typography variant="body2">Solo con video</Typography>}
              sx={{ ml: { sm: 0.5 } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
              {cargando ? "Cargando..." : `${filtradas.length} noticia${filtradas.length === 1 ? "" : "s"}`}
            </Typography>
          </Stack>

          {sucursales.length > 1 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Todas"
                size="small"
                color={sucursal ? "default" : "primary"}
                variant={sucursal ? "outlined" : "filled"}
                onClick={() => setSucursal("")}
              />
              {sucursales.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  color={sucursal === s ? "primary" : "default"}
                  variant={sucursal === s ? "filled" : "outlined"}
                  onClick={() => setSucursal(sucursal === s ? "" : s)}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {cargando ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={i}>
                <Skeleton variant="rounded" height={260} />
              </Grid>
            ))}
          </Grid>
        ) : filtradas.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {hayFiltros ? "Ninguna noticia coincide con los filtros." : "Todavía no hay noticias."}
            </Typography>
            {hayFiltros ? (
              <Button size="small" onClick={limpiarFiltros}>Quitar filtros</Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Publica la primera con "Nueva noticia".
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {destacada && (
              <Box sx={{ mb: 2.5 }}>
                <NoticiaCard noticia={destacada} destacada {...acciones} />
              </Box>
            )}

            <Grid container spacing={2.5}>
              {pageItems.map((noticia) => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={noticia.id}>
                  <NoticiaCard noticia={noticia} {...acciones} />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={cambiarPagina} color="primary" />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Modal para registrar/editar noticia */}
      <Modal open={openModal} onClose={handleModalClose}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 480, maxWidth: "90vw", bgcolor: "background.paper", p: 4, borderRadius: 2, maxHeight: "90vh", overflowY: "auto" }}>
          <RegisterNewsForm onSuccess={handleSuccess} noticia={editando} />
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

export default NoticiasPage;
